import {
  IPayeeResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useWSQueries, WSQueryCache } from "@ws-react-query";
import { useMemo } from "react";
import { QUERY_PAYEE } from "../../../../query/payee/keys";
import { getPayee } from "../../../../services/payees/getPayee";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";
import { usePayeeTaxVerificationConfig } from "./configs/payee/usePayeeTaxVerificationConfig";
import { usePayerTaxVerificationConfig } from "./configs/payer/usePayerTaxVerificationConfig";
import { TaxVerificationAdditionalData } from "./types";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const payeeQueries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.TaxVerification &&
        requirement.payeeId
    )
    .map(requirement => ({
      queryKey: [QUERY_PAYEE, requirement.payeeId],
      queryFn: () => getPayee(requirement.payeeId),
      ...sharedRequirementsQueriesConfig
    }));

  return useWSQueries({ queries: payeeQueries });
};

export const useTaxVerificationRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.TaxVerification] => {
  const payerTaxVerificationConfig = usePayerTaxVerificationConfig(callback);
  const payeeTaxVerificationConfig = usePayeeTaxVerificationConfig(callback);
  const contextQueries = useContextQueries(requirements);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    () =>
      buildConfigFields<
        TaxVerificationAdditionalData,
        RequirementType.TaxVerification
      >({
        payerConfig: payerTaxVerificationConfig,
        payeeConfig: payeeTaxVerificationConfig,
        getAdditionalData: requirement => {
          const payee = WSQueryCache.getQueryData<IPayeeResponse>([
            QUERY_PAYEE,
            requirement.payeeId
          ]);
          return { payee };
        }
      })(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payerTaxVerificationConfig, payeeTaxVerificationConfig, queriesHash]
  );

  return {
    ...fields,
    getAdditionalData: requirement => {
      const payee = WSQueryCache.getQueryData<IPayeeResponse>([
        QUERY_PAYEE,
        requirement.payeeId
      ]);
      return { payee };
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
